import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { commitLocalUpdate, commitMutation } from "react-relay";

import { differenceInDays } from "date-fns";
import { graphql } from "relay-runtime";
import store from "store2";

import { useLazyLoadQuery } from "~/lib/relayUtil";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { environment } from "~/models";
import { Button } from "~/ui";
import InlineBanner from "~/ui/banners/InlineBanner";
import toasty, { ToastActionButton } from "~/ui/toast";

import { useSyncedPersistentState } from "./hooks/useSyncedPersistentState";

import type { ArchiveActionItemsBannerMutation } from "~/__generated__/ArchiveActionItemsBannerMutation.graphql";
import type { ArchiveActionItemsBannerQuery } from "~/__generated__/ArchiveActionItemsBannerQuery.graphql";

import styles from "./ArchiveActionItemsBanner.module.css";

const DAYS_THRESHOLD = 60;

const BANNER_DISMISSAL_DELAY_DAYS = 14;

const ArchiveActionItemsBannerQueryDocument = graphql`
	query ArchiveActionItemsBannerQuery {
		viewer {
			toArchivedActionItemsCount
		}
	}
`;

const ArchiveActionItemsMutationDocument = graphql`
	mutation ArchiveActionItemsBannerMutation($days: Int!) {
		archiveActionItems(createdFromDays: $days) {
			updatedBlocks {
				id
				key
				text
				done
			}
		}
	}
`;

interface ArchiveActionItemsBannerProps {
	isPanel?: boolean;
}

const ACTION_ITEMS_ARCHIVE_BANNER = "ACTION_ITEMS_ARCHIVE_BANNER";

const actionItemsStore = store.namespace(ACTION_ITEMS_ARCHIVE_BANNER);

export default function ArchiveActionItemsBanner({ isPanel = false }: ArchiveActionItemsBannerProps) {
	const [dismissed, setDismissed] = useState(false);
	const [isArchiving, setIsArchiving] = useState(false);
	const [error, setError] = useState<Error | null>(null);
	const archiveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const [refetchKey, setRefetchKey] = useState(0); // Add a key to force refetch
	const [dismissedAt, setDismissedAt] = useSyncedPersistentState<Date | null>(actionItemsStore, "dismissedAt", null);

	const data = useLazyLoadQuery<ArchiveActionItemsBannerQuery>(
		ArchiveActionItemsBannerQueryDocument,
		{},
		{
			fetchPolicy: "store-and-network",
			fetchKey: refetchKey,
		},
	);

	const oldActionItemsCount = useMemo(
		() => (data?.viewer?.toArchivedActionItemsCount != null ? Number(data.viewer.toArchivedActionItemsCount) : 0),
		[data],
	);

	const wasDismissedRecently = useMemo(() => {
		if (dismissedAt) {
			const now = new Date();
			const daysSinceDismissed = differenceInDays(now, dismissedAt);
			// if the banner was dismissed less than 14 days ago, it should stay dismissed
			return daysSinceDismissed < BANNER_DISMISSAL_DELAY_DAYS;
		}

		// if there is no dismissedAt (meaning the banner was never dismissed), it should show the banner
		return false;
	}, [dismissedAt]);

	const handleDismiss = useCallback(() => {
		setDismissed(true);
		setDismissedAt(new Date());
		dispatchAnalyticsTrack("Action Items Archive Banner Actioned", {
			action: "Later",
		});
	}, [setDismissedAt]);

	const handleUndoArchive = useCallback(() => {
		if (archiveTimeoutRef.current) {
			clearTimeout(archiveTimeoutRef.current);
			archiveTimeoutRef.current = null;
			setIsArchiving(false);
		}
	}, []);

	const handleErrorDismiss = useCallback(() => {
		setError(null);
	}, []);

	const handleArchive = useCallback(() => {
		setIsArchiving(true);
		setError(null);

		dispatchAnalyticsTrack("Action Items Archive Banner Actioned", {
			action: "Archive",
		});

		toasty.info(`${oldActionItemsCount} ${oldActionItemsCount === 1 ? "Action item" : "Action items"} archived`, {
			autoClose: 5000,
			actions: (
				<ToastActionButton onClick={handleUndoArchive} variant="secondary">
					Undo
				</ToastActionButton>
			),
		});

		archiveTimeoutRef.current = setTimeout(() => {
			commitMutation<ArchiveActionItemsBannerMutation>(environment, {
				mutation: ArchiveActionItemsMutationDocument,
				variables: { days: DAYS_THRESHOLD },
				onCompleted: response => {
					setIsArchiving(false);
					if (response.archiveActionItems?.updatedBlocks) {
						setDismissed(true);

						setRefetchKey(prev => prev + 1);

						commitLocalUpdate(environment, store => {
							const root = store.getRoot();
							const actionItemGroups = root.getLinkedRecord("actionItemGroups");
							if (actionItemGroups) {
								root.setLinkedRecord(null, "actionItemGroups");
							}

							response.archiveActionItems?.updatedBlocks.forEach(block => {
								const record = store.get(block.id);
								if (record) {
									record.setValue(true, "wontDo");
								}
							});
						});

						window.dispatchEvent(new CustomEvent("action-items-updated"));

						window.dispatchEvent(new CustomEvent("action-items-refresh"));
					} else {
						setError(new Error(`Failed to archive ${oldActionItemsCount === 1 ? "action item" : "action items"}`));
					}
				},
				onError: error => {
					setIsArchiving(false);
					setError(error);
				},
			});
		}, 5000);
	}, [oldActionItemsCount, handleUndoArchive]);

	useEffect(() => {
		return () => {
			if (archiveTimeoutRef.current) {
				clearTimeout(archiveTimeoutRef.current);
			}
		};
	}, []);

	// Track when the banner is shown
	useEffect(() => {
		if (oldActionItemsCount > 0 && !dismissed && !wasDismissedRecently) {
			dispatchAnalyticsTrack("Action Items Archive Banner Shown");
		}
	}, [oldActionItemsCount, dismissed, wasDismissedRecently]);

	if (oldActionItemsCount === 0 || dismissed || wasDismissedRecently) {
		return null;
	}

	const bannerContent = error ? (
		<InlineBanner variant="error" dismissible className={styles.banner}>
			<div className={styles.bannerContent}>
				<span>
					Failed to archive {oldActionItemsCount === 1 ? "action item" : "action items"}. Please try again later.
				</span>
				<Button variant="tertiary" size="slim" onClick={handleErrorDismiss}>
					Dismiss
				</Button>
			</div>
		</InlineBanner>
	) : (
		<InlineBanner variant="default" dismissible={false} className={styles.banner} hideIcon>
			<div className={styles.bannerContent}>
				<span className={styles.bannerText}>
					You have{" "}
					<strong>
						{oldActionItemsCount} {oldActionItemsCount === 1 ? "action item" : "action items"}
					</strong>{" "}
					older than {DAYS_THRESHOLD} days. Archive {oldActionItemsCount === 1 ? "it" : "them"}?
				</span>
				<div className={styles.bannerActions}>
					<Button
						variant="primary"
						size="slim"
						onClick={handleArchive}
						disabled={isArchiving}
						className={styles.archiveButton}
					>
						{isArchiving ? "Archiving..." : "Archive"}
					</Button>
					<Button variant="tertiary" size="slim" onClick={handleDismiss} className={styles.dismissButton}>
						Later
					</Button>
				</div>
			</div>
		</InlineBanner>
	);

	return <div className={isPanel ? styles.panelBannerWrapper : styles.bannerWrapper}>{bannerContent}</div>;
}
